// src/components/PDFviewer_4_customer_versions.jsx
// Same invoice logic as your attached PDFviewer.jsx, with 4 totally different visual invoice styles.
// Usage examples:
//   <DocumentDetails id={invoiceId} variant="modernBlue" />
//   <DocumentDetails id={invoiceId} variant="luxuryBlack" />
//   <DocumentDetails id={invoiceId} variant="minimalClean" />
//   <DocumentDetails id={invoiceId} variant="warmCreative" />

import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  styled,
  tableCellClasses,
} from "@mui/material";
import axios from "axios";
import { useParams } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import { useReactToPrint } from "react-to-print";
import { Button, Modal } from "react-bootstrap";
import InvoiceEdit from "../InvoiceEdit";

const VARIANTS = {
  modernBlue: {
    name: "Modern Blue Corporate",
    pageBg: "#eef4fb",
    paperBg: "#ffffff",
    ink: "#102033",
    muted: "#64748b",
    primary: "#0f62fe",
    primaryDark: "#073b8e",
    accent: "#38bdf8",
    soft: "#e8f2ff",
    line: "#dbe7f3",
    tableHeadBg: "#0f62fe",
    tableHeadColor: "#ffffff",
    rowAlt: "#f4f9ff",
    bankBg: "#eff6ff",
    fontFamily: "Inter, Arial, sans-serif",
    layout: "hero",
  },
  luxuryBlack: {
    name: "Luxury Black & Gold",
    pageBg: "#151515",
    paperBg: "#fbf8ef",
    ink: "#1c1917",
    muted: "#6b5f4a",
    primary: "#b88a2f",
    primaryDark: "#3a2a12",
    accent: "#d8b45b",
    soft: "#f4ead2",
    line: "#dcc88d",
    tableHeadBg: "#1c1917",
    tableHeadColor: "#f9e7ad",
    rowAlt: "#f5edd9",
    bankBg: "#272016",
    bankColor: "#f8e7b0",
    fontFamily: "Georgia, 'Times New Roman', serif",
    layout: "editorial",
  },
  minimalClean: {
    name: "Minimal Clean Professional",
    pageBg: "#f7f7f5",
    paperBg: "#ffffff",
    ink: "#111827",
    muted: "#6b7280",
    primary: "#111827",
    primaryDark: "#000000",
    accent: "#9ca3af",
    soft: "#f4f4f5",
    line: "#e5e7eb",
    tableHeadBg: "#ffffff",
    tableHeadColor: "#111827",
    rowAlt: "#fafafa",
    bankBg: "#ffffff",
    fontFamily: "Arial, Helvetica, sans-serif",
    layout: "minimal",
  },
  warmCreative: {
    name: "Warm Creative Studio",
    pageBg: "#fff7ed",
    paperBg: "#fffbf2",
    ink: "#3f2a1d",
    muted: "#8a6a55",
    primary: "#ea580c",
    primaryDark: "#7c2d12",
    accent: "#facc15",
    soft: "#ffedd5",
    line: "#fed7aa",
    tableHeadBg: "#ea580c",
    tableHeadColor: "#fff7ed",
    rowAlt: "#fff1df",
    bankBg: "#431407",
    bankColor: "#fff7ed",
    fontFamily: "Trebuchet MS, Arial, sans-serif",
    layout: "creative",
  },
};

const money = (value) =>
  `$${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const safePaymentDetails = (invoice) => {
  try {
    return invoice?.paymentDetails ? JSON.parse(invoice.paymentDetails) : {};
  } catch {
    return {};
  }
};

const PrintBalancedStyles = () => (
  <style>{`
    @page {
      size: A4;
      margin: 0;
    }

    @media print {
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #fff !important;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
      }

      .no-print {
        display: none !important;
      }

      .invoice-print-paper {
        width: calc(100% - 20mm) !important;
        max-width: none !important;
        min-height: auto !important;
        margin: 10mm auto !important;
        box-shadow: none !important;
        border-radius: 0 !important;
      }

      .invoice-print-paper * {
        break-inside: avoid;
      }

      .invoice-print-paper table,
      .invoice-print-paper tbody,
      .invoice-print-paper tr {
        break-inside: auto;
      }

      .invoice-print-paper td,
      .invoice-print-paper th {
        padding: 5px 8px !important;
        line-height: 1.28 !important;
      }
    }
  `}</style>
);

const AddressRenderer = ({ address, color }) => {
  if (!address) return null;
  const parts = String(address)
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

  return (
    <div>
      {parts.map((part, index) => (
        <p style={{ margin: 0, color, lineHeight: 1.38 }} key={index}>
          {part}
        </p>
      ))}
    </div>
  );
};

function InvoiceModal(props) {
  return (
    <Modal
      {...props}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {props.modalInfo.title}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>{props.modalInfo.body}</Modal.Body>
      <Modal.Footer>
        <Button onClick={props.onHide}>Cancel</Button>
      </Modal.Footer>
    </Modal>
  );
}

const createStyles = (theme) => ({
  shell: {
    maxWidth: 900,
    width: "100%",
    margin: "0 auto",
    fontFamily: theme.fontFamily,
  },
  toolbar: {
    margin: "12px auto",
    display: "flex",
    justifyContent: "center",
    gap: 16,
  },
  toolbarButton: {
    border: 0,
    borderRadius: 999,
    padding: "10px 18px",
    fontWeight: 800,
    letterSpacing: "0.03em",
    background: theme.primary,
    color: "#fff",
    boxShadow: "none",
  },
  pageWrap: {
    background: theme.pageBg,
    padding: 14,
  },
  invoicePaper: {
    width: "100%",
    minHeight: "auto",
    background: theme.paperBg,
    color: theme.ink,
    borderRadius: theme.layout === "minimal" ? 0 : 14,
    overflow: "hidden",
    boxShadow: "none",
    border: `1px solid ${theme.line}`,
    position: "relative",
  },
  body: {
    padding: theme.layout === "minimal" ? "28px 34px" : "18px 22px",
  },
  logo: {
    width: 78,
    height: 78,
    objectFit: "cover",
    borderRadius:
      theme.layout === "luxuryBlack"
        ? 12
        : theme.layout === "minimal"
          ? 0
          : "50%",
    border: `2px solid ${theme.accent}`,
    background: "#fff",
    alignSelf: "center",
    marginRight: "12px",
  },
  businessName: {
    margin: 0,
    fontSize: theme.layout === "editorial" ? 27 : 26,
    fontWeight: theme.layout === "editorial" ? 500 : 900,
    letterSpacing: theme.layout === "minimal" ? "-0.03em" : "-0.01em",
    color: theme.ink,
  },
  mutedText: {
    margin: 0,
    color: theme.muted,
    fontSize: 12,
    lineHeight: 1.4,
  },
  label: {
    margin: 0,
    color: theme.muted,
    fontSize: 10,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.12em",
  },
  table: {
    marginTop: 18,
    marginBottom: 10,
    width: "100%",
    borderRadius: theme.layout === "minimal" ? 0 : 10,
    overflow: "hidden",
    boxShadow: "none",
    border: `1px solid ${theme.line}`,
  },
  totalBox: {
    marginLeft: "auto",
    marginTop: 12,
    width: 270,
    padding: "12px 16px",
    borderRadius: theme.layout === "minimal" ? 0 : 10,
    background: theme.primary,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontSize: 17,
    fontWeight: 900,
  },
  bankCard: {
    marginTop: 16,
    padding: 14,
    borderRadius: theme.layout === "minimal" ? 0 : 10,
    backgroundColor: theme.bankBg,
    color: theme.bankColor || theme.ink,
    border: `1px solid ${theme.line}`,
  },
});

const InvoiceHeader = ({ theme, styles, user, customer, invoice }) => {
  const logoSrc = user?.businessLogo ? `/${user.businessLogo}` : undefined;

  if (theme.layout === "hero") {
    return (
      <>
        <div
          style={{
            background: `linear-gradient(135deg, ${theme.primaryDark}, ${theme.primary})`,
            color: "#fff",
            padding: "16px 20px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <div>
            <p
              style={{
                margin: 0,
                fontSize: 11,
                opacity: 0.8,
                fontWeight: 800,
                letterSpacing: "0.16em",
              }}
            >
              TAX INVOICE
            </p>
            <h1 style={{ margin: "6px 0 0", fontSize: 36, fontWeight: 950 }}>
              {invoice.invoiceNumber}
            </h1>
            <p style={{ margin: "6px 0 0", fontSize: 12, opacity: 0.9 }}>
              Issued {new Date(invoice.date).toLocaleDateString()} · Due{" "}
              {invoice.dueDate}
            </p>
          </div>
          {logoSrc && (
            <img
              src={process.env.REACT_APP_API_URL + logoSrc}
              alt="Business Logo"
              style={styles.logo}
            />
          )}
        </div>
        <div
          style={{
            ...styles.body,
            display: "grid",
            gridTemplateColumns: "1.1fr 1fr",
            gap: 16,
          }}
        >
          <InfoCard title="From" heading={user.businessName} theme={theme}>
            <AddressRenderer
              address={user.businessAddress}
              color={theme.muted}
            />
          </InfoCard>
          <InfoCard title="Bill To" heading={customer.name} theme={theme}>
            <AddressRenderer address={customer.address} color={theme.muted} />
          </InfoCard>
        </div>
      </>
    );
  }

  if (theme.layout === "editorial") {
    return (
      <div style={styles.body}>
        <div
          style={{
            border: `2px solid ${theme.primary}`,
            padding: 16,
            display: "grid",
            gridTemplateColumns: "100px 1fr 175px",
            gap: 16,
            alignItems: "center",
          }}
        >
          {logoSrc && (
            <img src={logoSrc} alt="Business Logo" style={styles.logo} />
          )}
          <div>
            <p style={styles.label}>Premium tax invoice</p>
            <h1 style={styles.businessName}>{user.businessName}</h1>
            <AddressRenderer
              address={user.businessAddress}
              color={theme.muted}
            />
          </div>
          <div style={{ textAlign: "right" }}>
            <h2 style={{ margin: 0, fontSize: 21, color: theme.primaryDark }}>
              Invoice
            </h2>
            <p style={{ margin: "5px 0 0", fontSize: 13, fontWeight: 800 }}>
              {invoice.invoiceNumber}
            </p>
            <p style={styles.mutedText}>
              Date: {new Date(invoice.date).toLocaleDateString()}
            </p>
            <p style={styles.mutedText}>Due: {invoice.dueDate}</p>
          </div>
        </div>
        <div
          style={{
            marginTop: 16,
            padding: 16,
            background: theme.soft,
            borderLeft: `8px solid ${theme.primary}`,
          }}
        >
          <p style={styles.label}>Customer</p>
          <h2 style={{ margin: "5px 0 6px", fontSize: 19 }}>{customer.name}</h2>
          <AddressRenderer address={customer.address} color={theme.muted} />
        </div>
      </div>
    );
  }

  if (theme.layout === "minimal") {
    return (
      <div style={styles.body}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            borderBottom: `1px solid ${theme.line}`,
            paddingBottom: 18,
          }}
        >
          <div>
            <h1 style={{ ...styles.businessName, fontSize: 26 }}>
              {user.businessName}
            </h1>
            <AddressRenderer
              address={user.businessAddress}
              color={theme.muted}
            />
          </div>
          {logoSrc && (
            <img
              src={logoSrc}
              alt="Business Logo"
              style={{
                ...styles.logo,
                width: 70,
                height: 70,
                border: `1px solid ${theme.line}`,
              }}
            />
          )}
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 26,
            paddingTop: 18,
          }}
        >
          <div>
            <p style={styles.label}>Invoice to</p>
            <h2 style={{ margin: "6px 0", fontSize: 19 }}>{customer.name}</h2>
            <AddressRenderer address={customer.address} color={theme.muted} />
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <MetaBlock
              label="Invoice"
              value={invoice.invoiceNumber}
              theme={theme}
            />
            <MetaBlock label="Type" value="Tax Invoice" theme={theme} />
            <MetaBlock
              label="Date"
              value={new Date(invoice.date).toLocaleDateString()}
              theme={theme}
            />
            <MetaBlock label="Due Date" value={invoice.dueDate} theme={theme} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...styles.body, paddingBottom: 14 }}>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 90px", gap: 10 }}
      >
        <div
          style={{
            background: theme.soft,
            padding: 16,
            borderRadius: 14,
            border: `2px dashed ${theme.primary}`,
          }}
        >
          <p style={{ ...styles.label, color: theme.primaryDark }}>
            Tax Invoice
          </p>
          <h1
            style={{
              margin: "6px 0 0",
              fontSize: 34,
              fontWeight: 950,
              color: theme.primaryDark,
            }}
          >
            {invoice.invoiceNumber}
          </h1>
          <p style={{ ...styles.mutedText, marginTop: 6 }}>
            Created {new Date(invoice.date).toLocaleDateString()} · Due{" "}
            {invoice.dueDate}
          </p>
        </div>
        <div style={{ textAlign: "center" }}>
          {logoSrc && (
            <img
              src={logoSrc}
              alt="Business Logo"
              style={{ ...styles.logo, width: 92, height: 92 }}
            />
          )}
        </div>
      </div>
      <div
        style={{
          marginTop: 16,
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 16,
        }}
      >
        <InfoCard title="Business" heading={user.businessName} theme={theme}>
          <AddressRenderer address={user.businessAddress} color={theme.muted} />
        </InfoCard>
        <InfoCard title="Customer" heading={customer.name} theme={theme}>
          <AddressRenderer address={customer.address} color={theme.muted} />
        </InfoCard>
      </div>
    </div>
  );
};

const InfoCard = ({ title, heading, children, theme }) => (
  <div
    style={{
      padding: 14,
      borderRadius: theme.layout === "minimal" ? 0 : 10,
      background: theme.soft,
      border: `1px solid ${theme.line}`,
    }}
  >
    <p
      style={{
        margin: 0,
        color: theme.muted,
        fontSize: 10,
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.12em",
      }}
    >
      {title}
    </p>
    <h3 style={{ margin: "6px 0", fontSize: 17, color: theme.ink }}>
      {heading}
    </h3>
    {children}
  </div>
);

const MetaBlock = ({ label, value, theme }) => (
  <div style={{ borderBottom: `1px solid ${theme.line}`, paddingBottom: 8 }}>
    <p
      style={{
        margin: 0,
        color: theme.muted,
        fontSize: 10,
        fontWeight: 800,
        textTransform: "uppercase",
        letterSpacing: "0.1em",
      }}
    >
      {label}
    </p>
    <p
      style={{
        margin: "4px 0 0",
        fontSize: 12,
        fontWeight: 800,
        color: theme.ink,
      }}
    >
      {value}
    </p>
  </div>
);

const BankDetails = ({ invoice, user, styles, theme }) => {
  const payment = safePaymentDetails(invoice);
  const textColor = theme.bankColor || theme.ink;
  const mutedColor = theme.bankColor ? "rgba(255,255,255,0.74)" : theme.muted;

  const rows = [
    ["Bank Name", payment.bankName || user.bankName],
    ["Account Holder Name", payment.accountName || user.bankAccName],
    ["Account Number", payment.accountNumber || user.bankAccNumber],
    payment.IBAN || user.bankAccIBAN
      ? ["IBAN", payment.IBAN || user.bankAccIBAN]
      : null,
    ["Currency", payment.currency || user.currency],
  ].filter(Boolean);

  return (
    <Paper
      id="bankDetails"
      className="print-break bank-card"
      elevation={0}
      style={styles.bankCard}
    >
      <Typography
        variant="subtitle1"
        sx={{ fontWeight: "bold", mb: 0.75, fontSize: 14, color: textColor }}
      >
        Bank Details
      </Typography>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          marginLeft: "10px",
        }}
      >
        {rows.map(([label, value]) => (
          <Typography
            key={label}
            variant="body2"
            sx={{ fontSize: 12, lineHeight: 1.35, color: textColor }}
          >
            <strong style={{ color: mutedColor }}>{label}:</strong>{" "}
            {value || "—"}
          </Typography>
        ))}
      </div>
    </Paper>
  );
};

const DocumentDetails = ({ id, variant = "modernBlue" }) => {
  const { user } = useContext(UserContext);
  const contentToPrint = useRef(null);
  const [invoice, setInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [modalInfo, setModalInfo] = useState({ title: "", body: "" });
  const [refresh, setRefresh] = useState(false);

  const paramID = useParams().id;
  const invoiceId = id || paramID;
  const theme = VARIANTS[variant] || VARIANTS.modernBlue;
  const styles = createStyles(theme);

  const StyledTableCell = styled(TableCell)(() => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.tableHeadBg,
      color: theme.tableHeadColor,
      fontWeight: 900,
      fontSize: 14,
      padding: "6px 8px",
      textTransform: "uppercase",
      letterSpacing: "0.08em",
      borderBottom: `1px solid ${theme.line}`,
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      padding: "6px 8px",
      color: theme.ink,
      borderBottom: `1px solid ${theme.line}`,
    },
  }));

  const StyledTableRow = styled(TableRow)(() => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.rowAlt,
    },
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  const handlePrint = useReactToPrint({
    content: () => contentToPrint.current,
    documentTitle: invoice?.invoiceNumber || "Invoice",
    pageStyle: `
      @page {
        size: A4;
        margin: 0;
      }

      @media print {
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: #fff !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }

        .no-print {
          display: none !important;
        }

        .invoice-print-paper {
          width: calc(100% - 20mm) !important;
          max-width: none !important;
          min-height: auto !important;
          margin: 10mm auto !important;
          box-shadow: none !important;
          border-radius: 0 !important;
        }
        .bank-card {
        position: fixed !important;
        bottom: 50px !important;
        left: 20px !important;
        right: 20px !important;
        }
      }
    `,
  });

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        const token = localStorage.getItem("token");
        const fetchedInvoice = await axios.get(
          `${process.env.REACT_APP_API_URL}/invoices/invoice/${invoiceId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setInvoice(fetchedInvoice.data.invoice);
        setInvoiceItems(fetchedInvoice.data.invoiceItems);
      } catch (error) {
        console.error("Error fetching invoice details:", error);
      }
    };

    if (invoiceId) fetchInvoiceDetails();
  }, [invoiceId, modalShow]);

  useEffect(() => {
    const fetchCustomerDetails = async () => {
      if (!invoice?.CustomerId) return;
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/customers/customer/${invoice.CustomerId}`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setCustomer(response.data);
      } catch (error) {
        console.error("Error fetching customer details:", error);
      }
    };

    fetchCustomerDetails();
  }, [invoice, refresh]);

  if (!invoice || !customer) return null;

  return (
    <div style={styles.shell}>
      <PrintBalancedStyles />
      <div style={styles.toolbar} className="no-print">
        <button
          style={styles.toolbarButton}
          onClick={() => handlePrint(null, () => contentToPrint.current)}
        >
          PRINT
        </button>
        <button
          style={styles.toolbarButton}
          onClick={() => {
            setModalShow(true);
            setModalInfo({
              title: `${invoice.invoiceNumber}`,
              body: (
                <InvoiceEdit
                  invoice={invoice}
                  invoiceItems={invoiceItems}
                  setModalShow={setModalShow}
                  customer={customer}
                  setRefresh={setRefresh}
                />
              ),
            });
          }}
        >
          EDIT
        </button>
      </div>

      <div style={styles.pageWrap}>
        <div
          className="invoice-print-paper"
          style={styles.invoicePaper}
          ref={contentToPrint}
        >
          <InvoiceHeader
            theme={theme}
            styles={styles}
            user={user}
            customer={customer}
            invoice={invoice}
          />

          <div
            style={{
              ...styles.body,
              paddingTop: theme.layout === "hero" ? 0 : 10,
            }}
          >
            {invoice.desc && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: "bold",
                  mb: 0.75,
                  fontSize: 14,
                  color: theme.ink,
                }}
              >
                {invoice.desc}
              </Typography>
            )}

            <TableContainer
              component={Paper}
              elevation={0}
              style={styles.table}
            >
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Description</StyledTableCell>
                    {/* <StyledTableCell align="center">Units</StyledTableCell>
                    <StyledTableCell align="right">Unit Price</StyledTableCell>
                    <StyledTableCell align="right">Total Price</StyledTableCell> */}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invoiceItems?.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>{item.description}</StyledTableCell>
                      {/* <StyledTableCell align="center">
                        {item.units}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {money(item.unitPrice)}
                      </StyledTableCell>
                      <StyledTableCell align="right">
                        {money(item.totalPrice)}
                      </StyledTableCell> */}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <div style={styles.totalBox}>
              <span>Total</span>
              <span>{money(invoice.total)}</span>
            </div>

            <BankDetails
              invoice={invoice}
              user={user}
              styles={styles}
              theme={theme}
            />
          </div>
        </div>
      </div>

      <InvoiceModal
        show={modalShow}
        onHide={() => setModalShow(false)}
        modalInfo={modalInfo}
      />
    </div>
  );
};

export const ModernBlueInvoice = (props) => (
  <DocumentDetails {...props} variant="modernBlue" />
);
export const LuxuryBlackInvoice = (props) => (
  <DocumentDetails {...props} variant="luxuryBlack" />
);
export const MinimalCleanInvoice = (props) => (
  <DocumentDetails {...props} variant="minimalClean" />
);
export const WarmCreativeInvoice = (props) => (
  <DocumentDetails {...props} variant="warmCreative" />
);

export default DocumentDetails;
